import { reactive, readonly, inject, computed, ref, type Ref, type ComputedRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { AxiosInstance } from 'axios'

import type { PitcherInfo } from '@lib/types/app'
import type { User } from '@openapi'
import { APPS_DB } from '@lib/constants/appsDb.const'
import { useAppsDb } from '@lib/composables/appsDb.use'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'

export interface SmartFolderProperty {
  name: string
  display_name?: string
  accessor: string
  type: string
  description?: string
  display_names_by_value?: Record<string, string>
}

export interface FileById {
  id: string
  name: string
  created_at: string
  modified_at: string
  thumbnail_url: string
  favorite_id?: string
  metadata?: Record<string, any>
}

interface PathSegment {
  value: string
  property: SmartFolderProperty
}

interface SmartFolderConfig {
  enabledProperties: SmartFolderProperty[]
}

interface State {
  hierarchyLevels: SmartFolderProperty[]
  currentPath: PathSegment[]
  availableValues: Array<{ value: string; count: number }>
  matchingFiles: FileById[]
  isLoading: boolean
  isSavingConfig: boolean
  viewMode: 'folders' | 'files'
  totalCount: number
  page: number
  pageSize: number
}

const defaultProperties: SmartFolderProperty[] = [
  { name: 'content_type', display_name: 'Content Type', accessor: 'content_type', type: 'string' },
  { name: 'original_extension', display_name: 'File extension', accessor: 'original_extension', type: 'string' },
  {
    name: 'can_share',
    display_name: 'Can be shared',
    accessor: 'can_share',
    type: 'boolean',
    display_names_by_value: { true: 'Can be shared', false: 'Cannot be shared' },
  },
]

const state = reactive<State>({
  hierarchyLevels: [],
  currentPath: [],
  availableValues: [],
  matchingFiles: [],
  isLoading: false,
  isSavingConfig: false,
  viewMode: 'folders',
  totalCount: 0,
  page: 1,
  pageSize: 50,
})

export default function useFileSmartFolderStore() {
  const pitcherInfo = inject<Ref<PitcherInfo>>('pitcherInfo')
  const myUser = inject<Ref<User>>('myUser')
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios', ref())
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )
  const smartFolderRuleAttributes = computed(() => pitcherSettings.value?.smart_folder_rule_attributes?.files || [])
  const { getEntry, upsertEntry } = useAppsDb()
  const isSmartFoldersView = useLocalStorage<boolean>(
    `isSmartFoldersView:${pitcherInfo?.value?.instanceId}:${myUser?.value?.id}`,
    false,
  )

  if (!launchDarkly.value.enable_smart_folders_in_files) {
    // If the feature flag is not enabled, we don't want to show the smart folders view
    isSmartFoldersView.value = false
  }

  async function fetchData() {
    if (!pitcherInfo?.value?.instanceId || !pitcherInfo.value.apiOrigin || !restApiAxios.value) return

    try {
      state.isLoading = true
      const shouldMatch = state.currentPath.map((segment) => [segment.property.accessor, segment.value])
      const currentLevelAccessor =
        state.hierarchyLevels.length > 0
          ? state.hierarchyLevels[Math.min(state.currentPath.length, state.hierarchyLevels.length - 1)].accessor
          : defaultProperties[0].accessor

      const orgSubDomain = window.location.hostname.split('.')?.[0]

      // TODO: proper response type
      const data = await restApiAxios.value.post<unknown, Record<string, any>>(
        `${pitcherInfo.value.apiOrigin}/core/api/protected/files/smart-folders`,
        {
          org_subdomain: orgSubDomain === 'localhost' ? 'dev' : orgSubDomain,
          owned_by_id: myUser?.value?.id,
          instance_id: pitcherInfo.value.instanceId,
          should_match: shouldMatch,
          folder_level: currentLevelAccessor,
          distinct: state.viewMode === 'folders',
          page: state.page,
          page_size: state.pageSize,
        },
      )

      if (state.viewMode === 'folders') {
        if (!data.distinct_values?.length) {
          // No folders found, switch to files view and fetch
          state.viewMode = 'files'
          return fetchData() // Recursive call with new viewMode
        }
        state.availableValues = data.distinct_values
        state.totalCount = data.distinct_values.length
      } else {
        state.matchingFiles = data.files
        state.totalCount = data.total_count
      }
    } catch (error) {
      console.error('Error fetching smart folder data:', error)
    } finally {
      state.isLoading = false
    }
  }

  async function loadConfiguration() {
    if (!myUser?.value) return

    try {
      state.isLoading = true
      const config = await getEntry({
        store_name: APPS_DB.STORES.FILE_SMART_FOLDERS,
        entry_key: APPS_DB.ENTRIES.FILE_SMART_FOLDERS,
        user_id: myUser.value.id,
      })

      if (config?.value) {
        state.hierarchyLevels = (config.value as SmartFolderConfig).enabledProperties
      } else {
        state.hierarchyLevels = []
      }
    } catch (error) {
      console.error('Error loading configuration:', error)
    } finally {
      state.isLoading = false
    }
  }

  async function saveConfiguration(config: SmartFolderConfig) {
    if (!myUser?.value) return

    try {
      state.isSavingConfig = true
      await upsertEntry({
        store_name: APPS_DB.STORES.FILE_SMART_FOLDERS,
        entry_key: APPS_DB.ENTRIES.FILE_SMART_FOLDERS,
        user_id: myUser.value.id,
        data: config,
      })
      state.hierarchyLevels = config.enabledProperties
      state.currentPath = []
      state.viewMode = 'folders'
      await fetchData()
    } finally {
      state.isSavingConfig = false
    }
  }

  function navigateToLevel(index: number) {
    state.viewMode = index === state.hierarchyLevels.length ? 'files' : 'folders'
    state.currentPath = state.currentPath.slice(0, index)
    state.page = 1
    fetchData()
  }

  function setCurrentPath(value: PathSegment[]) {
    state.currentPath = value
  }

  function selectValue(value: string, { skipFetchData = false }: { skipFetchData?: boolean } = {}) {
    if (state.currentPath.length >= state.hierarchyLevels.length) return

    state.currentPath.push({
      value,
      property: state.hierarchyLevels[state.currentPath.length],
    })

    if (state.currentPath.length === state.hierarchyLevels.length) {
      state.viewMode = 'files'
    }

    if (!skipFetchData) {
      fetchData()
    }
  }

  function toggleViewMode() {
    state.viewMode = state.viewMode === 'folders' ? 'files' : 'folders'
    fetchData()
  }

  function reset() {
    state.currentPath = []
    state.availableValues = []
    state.matchingFiles = []
    state.page = 1
    fetchData()
  }

  function setMatchingFiles(files: FileById[]) {
    state.matchingFiles = files
  }

  return {
    state: readonly(state),
    isSmartFoldersView,
    defaultProperties,
    allSmartFolderProperties: computed(() => [...defaultProperties, ...smartFolderRuleAttributes.value]),
    actions: {
      fetchData,
      loadConfiguration,
      saveConfiguration,
      navigateToLevel,
      setCurrentPath,
      selectValue,
      toggleViewMode,
      reset,
      setMatchingFiles,
    },
  }
}
